import { useTranslation } from 'react-i18next'

import { AppliedFilters, EmptyState, SortBy } from '@/components'
import { DEFAULT_LIMIT_PAGE, MERCAI_WAREHOUSES_OWNER_EMAIL } from '@/constants'
import {
  formatCurrency,
  formatDate,
  formatNumber,
  useParamsState,
} from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import {
  useCatalogProductDetail,
  useInventoryList,
  useSupplierDetail,
} from '@/services'
import { InventoryProps } from '@/types'
import {
  ArrowRightIcon,
  Button,
  FilterIcon,
  InputSearch,
  Pagination,
  Select,
  TableColumns,
  TableColumnsColumnProps,
  Tag,
  Text,
  Tooltip,
} from '@mercai/clever'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { GeneratePurchaseOrderDrawerFilter } from './components'
import styles from './table-inventory.module.css'

export const TableInventory = () => {
  const { t } = useTranslation('screenInventoryList')

  const [filter, setFilter] = useParamsState({
    page: '1',
    limit: `${DEFAULT_LIMIT_PAGE}`,
    search: '',
    status: '',
    ordering: 'created_at',
  })

  const { data, isLoading } = useInventoryList({ params: filter })

  const { data: catalogProductDetail } = useCatalogProductDetail(
    filter?.catalogProductUuid || '',
  )

  const { data: supplierDetail } = useSupplierDetail(filter?.supplierUuid || '')

  const [totalPages, setTotalPages] = useState(1)

  const [isOpenDrawerFilter, setIsOpenDrawerFilter] = useState(false)

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) =>
        !!filter[key] &&
        key !== 'page' &&
        key !== 'limit' &&
        key !== 'ordering',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  const handleFilter = (changedKey: string, newValue: string | number) => {
    if (changedKey === 'page') {
      setFilter({ ...filter, [changedKey]: `${newValue}` })
    } else {
      setFilter({ ...filter, [changedKey]: `${newValue}`, page: '1' })
    }
  }

  const onHandleSortBy = (ordering: string) => {
    if (ordering === filter.ordering) {
      return handleFilter('ordering', '')
    }

    handleFilter('ordering', ordering)
  }

  const columns: TableColumnsColumnProps<InventoryProps>[] = [
    {
      title: t('productColumn'),
      key: 'catalog-product',
      render: ({ record }) => (
        <div className={styles['column-supplier']}>
          <Tooltip
            text={record?.catalogProduct?.attributes?.title || '-'}
            orientation="top"
          >
            <Link
              target="_blank"
              to={ROUTES_ADMIN.CATALOG.PRODUCT.SHOW.fullPath(
                record?.catalogProduct?.uuid || '',
              )}
              className={styles.link}
            >
              {record?.catalogProduct?.attributes?.title || '-'}
            </Link>
          </Tooltip>
        </div>
      ),
    },
    {
      title: t('quantityColumn'),
      key: 'quantity',
      width: '10rem',
      render: ({ record }) => (
        <Text>{formatNumber(record.quantity || '0', undefined, 0)} KG</Text>
      ),
    },
    {
      title: t('costColumn'),
      key: 'cost',
      width: '10rem',
      render: ({ record }) => (
        <Text>
          {record?.cost
            ? formatCurrency(record.cost, 'es-MX', record.currency || 'USD')
            : '0'}
        </Text>
      ),
    },
    {
      title: t('lastMovementColumn'),
      key: 'last-movement',
      width: '8rem',
      render: ({ record }) => (
        <Text>
          {record?.lastInventoryTransaction?.updatedAt
            ? formatDate(record?.lastInventoryTransaction?.updatedAt)
            : '-'}
        </Text>
      ),
    },
    {
      title: t('supplierColumn'),
      key: 'supplier',
      width: '14rem',
      render: ({ record }) => (
        <div className={styles['column-supplier']}>
          {record?.supplier ? (
            <Tooltip text={record?.supplier?.name || '-'} orientation="top">
              <Link
                target="_blank"
                to={ROUTES_ADMIN.SUPPLY.SUPPLIERS.SUPPLIERS.SHOW.fullPath(
                  record?.supplier?.uuid || '',
                )}
                className={styles.link}
              >
                {record?.supplier?.name || '-'}
              </Link>
            </Tooltip>
          ) : (
            <Text>-</Text>
          )}
        </div>
      ),
    },
    {
      title: t('warehouseColumn'),
      key: 'warehouse',
      width: '10rem',
      render: ({ record }) => (
        <div className={styles['column-supplier']}>
          {record?.supplierWarehouse ? (
            <Text>{record?.supplierWarehouse?.name || '-'}</Text>
          ) : (
            <Text>-</Text>
          )}
        </div>
      ),
    },
    {
      title: t('deliveryOptionColumn'),
      key: 'delivery-option',
      width: '8rem',
      render: ({ record }) => (
        <div className={styles['column-supplier']}>
          {record?.supplier ? (
            record?.supplier?.email === MERCAI_WAREHOUSES_OWNER_EMAIL ? (
              <Tag variant="success" model="light">
                {t('deliveryOptionDeliveryOptionDeliverToOurWarehouse')}
              </Tag>
            ) : (
              <Tag variant="info" model="light">
                {t('deliveryOptionDeliveryOptionKeepAtSupplier')}
              </Tag>
            )
          ) : (
            <Text>-</Text>
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: '5rem',
      render: ({ record }) => (
        <Button
          href={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.INVENTORY.SHOW.fullPath(
            record?.uuid || '',
          )}
          model="round"
        >
          <ArrowRightIcon />
        </Button>
      ),
    },
  ]

  const sorts = [
    {
      title: t('orderingCreatedAtButton'),
      sortBy: 'created_at',
    },
  ]

  useEffect(() => {
    if (data?.data?.count) {
      setTotalPages(
        Math.ceil(data?.data?.count / +(filter?.limit || DEFAULT_LIMIT_PAGE)),
      )
    }
  }, [data])

  return (
    <div className={styles.core}>
      <div className={styles.filter}>
        <div className={styles['filter-group']}>
          <InputSearch
            value={filter.search as string}
            onChange={(value) => handleFilter('search', value)}
            className={styles['search-input']}
            translations={{
              inputPlaceholder: t('tableSearchPlaceholder'),
            }}
          />

          <Button
            variant="secondary"
            onClick={() => setIsOpenDrawerFilter(true)}
          >
            <FilterIcon />
            {t('filterButton')}
            {getContentTotalFiltered()}
          </Button>

          <SortBy
            sortBy={filter?.ordering}
            sorts={sorts}
            onHandleSortBy={onHandleSortBy}
          />
        </div>
      </div>

      <AppliedFilters
        filtersApplied={filter}
        excludeKeys={['page', 'limit', 'ordering']}
        onRemoveFilter={(key) => handleFilter(key, '')}
        translations={{
          filterAppliedText: t('filterApplied'),
        }}
        keyTranslations={{
          catalogProductUuid: t('filterCatalogProductUuid'),
          supplierUuid: t('filterSupplierUuid'),
          deliveryOption: t('filterDeliveryOption'),
          rootCategory: t('filterRootCategory'),
        }}
        renderValue={(key, value) => {
          if (key === 'catalogProductUuid') {
            return catalogProductDetail?.data?.attributes?.title || ''
          }

          if (key === 'supplierUuid') {
            return supplierDetail?.data?.name || ''
          }

          if (key === 'deliveryOption') {
            return value === 'keep_at_supplier'
              ? t('filterDeliveryOptionKeepAtSupplier')
              : t('filterDeliveryOptionDeliverToOurWarehouse')
          }

          if (key === 'rootCategory') {
            return value === 'Metais'
              ? t('filterRootCategoryMetales')
              : t('filterRootCategoryResinasPlasticas')
          }

          return value
        }}
      />

      <TableColumns
        isLoading={isLoading}
        columns={columns}
        data={data?.data?.results || []}
        onSortBy={onHandleSortBy}
        sortBy={filter?.ordering}
      />

      {!isLoading && (!data?.data || data?.data?.results?.length === 0) && (
        <EmptyState
          title={t('tableEmptyTitle')}
          description={t('tableEmptyDescription')}
        />
      )}

      {data?.data?.results && data.data.results.length >= 1 && (
        <div className={styles.pagination}>
          <div className={styles['pagination-show-registers']}>
            <Text>
              {t('tableShowRegisters', {
                showNumber:
                  (data?.data?.count || 0) < +filter.limit
                    ? data?.data?.count
                    : +filter.limit,
                totalNumber: data?.data?.count || 0,
              })}
            </Text>

            <Select
              value={filter.limit}
              onChange={(value) => handleFilter('limit', value)}
              options={[
                { label: '10', value: '10' },
                { label: '20', value: '20' },
                { label: '50', value: '50' },
                { label: '100', value: '100' },
              ]}
            />
          </div>

          <Pagination
            totalPages={totalPages}
            onChange={(page) => handleFilter('page', page)}
            currentPage={+filter.page as number}
          />
        </div>
      )}

      <GeneratePurchaseOrderDrawerFilter
        filter={filter}
        onHandleFilter={setFilter}
        isOpen={isOpenDrawerFilter}
        onHandleOpen={() => setIsOpenDrawerFilter(false)}
      />
    </div>
  )
}
